import React, { useEffect, useMemo, useRef, useState } from "react";
import { updateRefClick } from "../../../../Utils/SupabaseServices/ContentDatabase";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { TEMPLATE_TRACKING_TYPES } from "../../../../Utils/SupabaseServices/TrackTemplate";
import type { TemplateShopView, ThemeTokens } from "../types";
import type { ShopCatalogProduct } from "../templateShopCatalog";
import { shouldShowProductBrand } from "../shopBreadcrumbs";
import { TemplateTabFeedPendingGrid } from "../TemplateTabFeedPendingGrid";
import { TemplateRevealImage } from "../TemplateRevealImage";
import { TemplateFeedMediaRevealRoot } from "../TemplateFeedMediaRevealRoot";
import {
  findTemplateBatchAnchor,
  scheduleScrollTemplateNewBatchIntoView,
} from "../scrollTemplateNewBatchIntoView";
import {
  TemplateMasonryGrid,
  useRiseAfterCatalogLoader,
} from "../TemplateMasonryGrid";
import { preloadBudgetedShopImages } from "../../../../Utils/templateImageDelivery";
import {
  productMediaRatioFromId,
  productPackWeight,
  safeMediaRatio,
} from "../templateMasonry";
import { ShopCatalogEmpty } from "../ShopBrowseNav";
import { resolveFeedScrollRoot } from "../../../../Utils/feedScrollRoot";
import { useEdit, E } from "../InlineEdit";
import { sanitizePublicCardTitle } from "../../../../Utils/media/feedCardTitle";
import { useCreatorData } from "../templateData";
import { addHttpToLink } from "../../../../Utils/StayUpToDate/RenderComponent";
import {
  PRODUCT_CARD_MAX_HEIGHT_PX,
  syncProductCardMaxHeightBg,
  useProductCardMaxHeightBg,
} from "../productCardMaxHeight";
import "../templateMediaReveal.css";

/** The product grid rises once per page visit, including after a route remount. */
let productCatalogIntroSeen = false;

export {
  PRODUCT_CARD_MAX_HEIGHT_PX,
  syncProductCardMaxHeightBg,
  useProductCardMaxHeightBg,
};

// How many products to reveal per scroll trigger.
const SHOP_INITIAL_BATCH = 25;
const SHOP_CHUNK_SIZE = 25;
const SHOP_MOBILE_INITIAL_BATCH = 10;

type ShopProductCatalogCardProps = {
  product: ShopCatalogProduct & { ratio?: number };
  index: number;
  isMobile: boolean;
  showButton: boolean;
  showCategory: boolean;
  showPrice: boolean;
  buttonLabel: string;
  brandCounts?: Map<string, number>;
  onShopClick?: () => void;
};

/** Shared product tile — used by Products page and mixed Search masonry. */
export const ShopProductCatalogCard = React.memo(function ShopProductCatalogCard({
  product: p,
  index,
  isMobile,
  showButton,
  showCategory,
  showPrice,
  buttonLabel,
  brandCounts,
  onShopClick,
}: ShopProductCatalogCardProps) {
  const { editable, get } = useEdit();
  const CREATOR = useCreatorData();
  const creatorUsername = String(CREATOR.bio?.username || "").trim();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const brandLabel = p.breadcrumbs?.brand?.trim() || "";
  const brandIsUsername =
    Boolean(creatorUsername) &&
    brandLabel.replace(/^@+/, "").trim().toLowerCase() ===
      creatorUsername.toLowerCase();
  const showBrand =
    showCategory &&
    Boolean(brandLabel) &&
    !brandIsUsername &&
    shouldShowProductBrand(p, brandCounts ?? new Map());
  const liveName = String(
    get(`product.${p.postId}.name`, p.name || "") || p.name || "",
  ).trim();
  const displayName = sanitizePublicCardTitle(
    liveName,
    {
      type: "PRODUCT",
      platform: p.platform,
      product_name: p.name,
      name: p.name,
      username: creatorUsername,
    },
    { creatorUsername },
  );
  // Weight-only estimate for masonry packing (no CSS aspect-ratio on products).
  const packRatio =
    p.ratio != null
      ? safeMediaRatio(p.ratio)
      : safeMediaRatio(productMediaRatioFromId(p.postId));

  useProductCardMaxHeightBg(frameRef, true, [p.image, p.postId]);

  const href = addHttpToLink(String(p.url || "").trim());
  const canOpen = Boolean(href && href !== "#" && !editable);

  return (
    <a
      href={canOpen ? href : undefined}
      className="mte-tpl-shop-card-link"
      data-mte-card="product"
      style={
        {
          ["--mte-tile-stagger" as string]: String(Math.min(index, 14)),
          // Kept for masonry weight math elsewhere; CSS no longer uses aspect-ratio.
          ["--tpl-card-aspect" as string]: String(packRatio),
        } as React.CSSProperties
      }
      target={canOpen ? "_blank" : undefined}
      rel={canOpen ? "noopener noreferrer" : undefined}
      onClick={(e) => {
        if (!canOpen) {
          e.preventDefault();
          return;
        }
        onShopClick?.();
        // Desktop only — blur sticky focus/hover chrome after target=_blank.
        // Don't blur on touch: some mobile browsers cancel navigation mid-tap.
        if (
          typeof window !== "undefined" &&
          window.matchMedia("(hover: hover) and (pointer: fine)").matches
        ) {
          (e.currentTarget as HTMLAnchorElement).blur();
        }
      }}
    >
      <div
        ref={frameRef}
        className="mte-tpl-shop-card-frame mte-tpl-shop-card-frame--mb14"
      >
        {p.image ? (
          <TemplateRevealImage
            src={p.image}
            alt=""
            index={index}
            priority={index < (isMobile ? 6 : 12)}
            deliveryRole="shop"
            className="mte-tpl-img-filter mte-tpl-shop-card-img"
            onImgLoad={(img) => {
              syncProductCardMaxHeightBg(frameRef.current, img);
            }}
          />
        ) : null}
        {showButton ? (
          <span className="mte-tpl-shop-pill mte-tpl-shop-pill--mobile">
            {buttonLabel.toUpperCase()}
          </span>
        ) : null}
      </div>
      {showBrand ? (
        <div className="mte-tpl-prod-cat" title={brandLabel}>
          {brandLabel}
        </div>
      ) : null}
      {displayName || (showPrice && (p as { price?: string }).price) ? (
        <div className="mte-tpl-prod-name-row mte-tpl-prod-name-row--no-btn">
          {displayName ? (
            <div className="mte-tpl-prod-name" title={displayName}>
              {editable ? (
                <E
                  path={`product.${p.postId}.name`}
                  className="mte-tpl-prod-name-edit"
                >
                  {displayName}
                </E>
              ) : (
                displayName
              )}
            </div>
          ) : null}
          {showPrice && (p as { price?: string }).price ? (
            <div className="mte-tpl-prod-price">
              {(p as { price?: string }).price}
            </div>
          ) : null}
        </div>
      ) : null}
    </a>
  );
});

type ShopProductCatalogPanelProps = {
  T: ThemeTokens;
  products: ShopCatalogProduct[];
  loading: boolean;
  userId: string;
  cols: number;
  aspect: string;
  showButton: boolean;
  showCategory: boolean;
  showPrice?: boolean;
  buttonLabel: string;
  sectionTitle?: string;
  shopView?: TemplateShopView;
  /** Stable identity for the current catalog (tab/browse) — resets scroll without remount. */
  catalogKey?: string;
  browseLabels?: {
    primary?: string | null;
    secondary?: string | null;
    sub?: string | null;
    brand?: string | null;
  };
  /** Brand → product count across the full catalog (for hiding sparse brand names). */
  brandCounts?: Map<string, number>;
  /** Search: parent concatenates pages — don't window or scroll-jump tiles. */
  disableInnerPagination?: boolean;
  /** Demand-driven network pagination (RPC pages). */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Filter change with previous tiles still on screen. */
  refreshing?: boolean;
};

type MasonryProduct = ShopCatalogProduct & {
  key: string;
  weight: number;
  ratio: number;
};

export function ShopProductCatalogPanel({
  T,
  products,
  loading,
  userId,
  cols,
  aspect: _aspect,
  showButton,
  showCategory,
  showPrice = false,
  buttonLabel,
  sectionTitle,
  shopView,
  catalogKey,
  browseLabels,
  brandCounts,
  disableInnerPagination = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  refreshing = false,
}: ShopProductCatalogPanelProps) {
  void _aspect;
  const templateAnalytics = useTemplateAnalyticsOptional();
  const isMobile = T.device === "mobile";
  const networkPaging = Boolean(onLoadMore);
  const initialBatch = isMobile ? SHOP_MOBILE_INITIAL_BATCH : SHOP_INITIAL_BATCH;

  const [visibleCount, setVisibleCount] = useState(initialBatch);
  const [isRevealingBatch, setIsRevealingBatch] = useState(false);
  // Skip first-paint stagger — skeleton → faded tiles reads as flicker.
  const [playEnterAnim, setPlayEnterAnim] = useState(false);
  const [appliedCatalogKey, setAppliedCatalogKey] = useState(catalogKey);
  const [arriving, setArriving] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);
  const productsLenRef = useRef(products.length);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  const prevResultsSigRef = useRef<string | null>(null);
  const heldSigRef = useRef<string | null>(null);
  const [riseHold, setRiseHold] = useState(() => productCatalogIntroSeen);
  productsLenRef.current = products.length;
  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;
  onLoadMoreRef.current = onLoadMore;

  // Hold the current window until new rows land, then reset in the same
  // render as the swap so the grid doesn't flash 25 → 10 tiles.
  const catalogNeedsApply = !refreshing && appliedCatalogKey !== catalogKey;
  if (catalogNeedsApply) {
    setAppliedCatalogKey(catalogKey);
    setVisibleCount(initialBatch);
    setIsRevealingBatch(false);
    isLoadingRef.current = false;
    if (prevResultsSigRef.current !== null && !riseHold) {
      heldSigRef.current = prevResultsSigRef.current;
      setRiseHold(true);
    }
  }
  const shownCount = catalogNeedsApply ? initialBatch : visibleCount;

  const resultsSig = products
    .slice(0, 8)
    .map((p) => p.postId)
    .join("\0");
  let arriveThisPaint = arriving;
  if (!loading && !refreshing) {
    if (prevResultsSigRef.current === null) {
      prevResultsSigRef.current = resultsSig;
      if (resultsSig && !productCatalogIntroSeen) {
        productCatalogIntroSeen = true;
        arriveThisPaint = true;
        setArriving(true);
        setPlayEnterAnim(true);
      }
    } else if (resultsSig !== prevResultsSigRef.current) {
      prevResultsSigRef.current = resultsSig;
    }
  }

  useEffect(() => {
    if (!arriving) return;
    setPlayEnterAnim(true);
    const t = window.setTimeout(() => {
      setArriving(false);
      setPlayEnterAnim(false);
    }, 960);
    return () => window.clearTimeout(t);
  }, [arriving]);

  // Category changes swap the cards. Hold the rise for that paint only so
  // closing a subcategory does not replay the intro. Scroll batches mount later.
  // A skeleton still has to rise: riseHold is true on the first data paint
  // after the loader, which is the handoff useRiseAfterCatalogLoader covers.
  useEffect(() => {
    if (!riseHold) return;
    if (!resultsSig || resultsSig === heldSigRef.current) return;
    const id = window.requestAnimationFrame(() => setRiseHold(false));
    return () => window.cancelAnimationFrame(id);
  }, [riseHold, resultsSig]);

  const showingLoader = (loading || refreshing) && products.length === 0;
  const riseAfterLoader = useRiseAfterCatalogLoader(
    showingLoader,
    products.length > 0,
  );

  // Keep one page ahead in memory. Do not paint it until the user scrolls.
  useEffect(() => {
    if (disableInnerPagination || !networkPaging || !hasMore || loadingMore) return;
    if (products.length === 0) return;
    if (products.length >= shownCount + SHOP_CHUNK_SIZE) return;
    onLoadMore?.();
  }, [
    disableInnerPagination,
    networkPaging,
    hasMore,
    loadingMore,
    products.length,
    shownCount,
    onLoadMore,
  ]);

  useEffect(() => {
    if (disableInnerPagination) return;
    const next = products.slice(shownCount, shownCount + SHOP_CHUNK_SIZE);
    preloadBudgetedShopImages(next.map((p) => p.image));
  }, [disableInnerPagination, products, shownCount]);

  const visibleProducts = useMemo(
    () =>
      disableInnerPagination
        ? products
        : products.slice(0, shownCount),
    [products, shownCount, disableInnerPagination],
  );

  const masonryItems = useMemo<MasonryProduct[]>(
    () =>
      visibleProducts.map((p) => {
        const ratio = safeMediaRatio(productMediaRatioFromId(p.postId));
        return {
          ...p,
          key: p.postId,
          ratio,
          weight: productPackWeight(ratio),
        };
      }),
    [visibleProducts],
  );

  const impressedIdsRef = useRef(new Set<string>());
  const impressedCatalogKeyRef = useRef(catalogKey);
  useEffect(() => {
    if (impressedCatalogKeyRef.current !== catalogKey) {
      impressedCatalogKeyRef.current = catalogKey;
      impressedIdsRef.current = new Set();
    }
    if (!templateAnalytics?.enabled) return;
    if (templateAnalytics.pageKey !== "shop") return;
    const newProducts = visibleProducts.filter(
      (p) => p.postId && !impressedIdsRef.current.has(p.postId),
    );
    if (newProducts.length === 0) return;
    newProducts.forEach((p) => impressedIdsRef.current.add(p.postId));
    templateAnalytics.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.PRODUCT_IMPRESSION, {
      sectionId: "productGrid",
      sectionType: "ProductGrid",
      entityType: "product",
      eventType: TEMPLATE_TRACKING_TYPES.RENDER,
      details: {
        post_ids: newProducts.map((p) => p.postId),
        batch_size: newProducts.length,
        shop_view: shopView,
      },
    });
  }, [visibleProducts, templateAnalytics, shopView, catalogKey]);

  useEffect(() => {
    if (disableInnerPagination) return;
    if (observerRef.current) observerRef.current.disconnect();

    const hasRows = products.length > 0;
    const canRevealLocal = shownCount < products.length;
    const canFetchNetwork =
      networkPaging && hasMore && shownCount >= products.length;
    if (!hasRows || (!canRevealLocal && !canFetchNetwork)) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const nested = resolveFeedScrollRoot();
    const scrollRoot = nested instanceof HTMLElement ? nested : null;

    let revealTimer = 0;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (isLoadingRef.current) return;

        // Paint one already-fetched chunk. Extra pages stay in memory.
        if (shownCount < productsLenRef.current) {
          isLoadingRef.current = true;
          setIsRevealingBatch(true);
          const batchStart = shownCount;
          revealTimer = window.setTimeout(() => {
            revealTimer = 0;
            setVisibleCount((prev) =>
              Math.min(prev + SHOP_CHUNK_SIZE, productsLenRef.current),
            );
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            if (!isMobile) {
              scheduleScrollTemplateNewBatchIntoView(
                () => findTemplateBatchAnchor(gridRef.current, batchStart),
                { behavior: "auto" },
              );
            }
          }, 1100);
          return;
        }

        if (!hasMoreRef.current) return;
        isLoadingRef.current = true;
        setIsRevealingBatch(true);
        if (networkPaging && !loadingMoreRef.current) onLoadMoreRef.current?.();
        const started = Date.now();
        const batchStart = shownCount;
        const tick = () => {
          const ready = batchStart < productsLenRef.current;
          const elapsed = Date.now() - started;
          if (ready && elapsed >= 1100) {
            revealTimer = 0;
            setVisibleCount((prev) =>
              Math.min(prev + SHOP_CHUNK_SIZE, productsLenRef.current),
            );
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            if (!isMobile) {
              scheduleScrollTemplateNewBatchIntoView(
                () => findTemplateBatchAnchor(gridRef.current, batchStart),
                { behavior: "auto" },
              );
            }
            return;
          }
          if (
            !ready &&
            ((!hasMoreRef.current && !loadingMoreRef.current) || elapsed > 8000)
          ) {
            revealTimer = 0;
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            return;
          }
          revealTimer = window.setTimeout(tick, 80);
        };
        revealTimer = window.setTimeout(tick, 80);
      },
      {
        root: scrollRoot ?? null,
        rootMargin: isMobile ? "0px 0px" : "50px 0px",
        threshold: 0,
      },
    );
    observerRef.current.observe(sentinel);
    return () => {
      if (revealTimer) {
        window.clearTimeout(revealTimer);
        isLoadingRef.current = false;
        setIsRevealingBatch(false);
      }
      observerRef.current?.disconnect();
    };
  }, [
    shownCount,
    products.length > 0,
    hasMore,
    disableInnerPagination,
    networkPaging,
    isMobile,
  ]);

  // Grey tiles while a filter page is in flight — don't freeze the old photos.
  if ((loading || refreshing) && products.length === 0) {
    return (
      <div
        className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--shop mte-tpl-shop-catalog-skeleton"
        aria-busy="true"
        data-website-tour="shop-results"
      >
        <TemplateTabFeedPendingGrid
          pageVariant="shop"
          device={T.device}
          T={T}
        />
      </div>
    );
  }

  if (products.length === 0) {
    const filtered = Boolean(
      browseLabels?.primary ||
        browseLabels?.secondary ||
        browseLabels?.sub ||
        browseLabels?.brand,
    );
    return (
      <div data-website-tour="shop-results">
        <ShopCatalogEmpty
          title={filtered ? "Nothing in this category" : "No products yet"}
          message={
            filtered
              ? "Try another category, or clear filters to see everything in the shop."
              : "When you add affiliate products, they'll show up here automatically. Connect a shop or drop in product links to get started."
          }
        />
      </div>
    );
  }

  // Search owns paging + outer `plainMedia` root — don't nest a reveal queue
  // (that blank→popped tiles and fought aspect-ratio CSS during infinite scroll).
  const grid = (
      <>
      {sectionTitle ? (
        <div className="mte-tpl-carousel-title">{sectionTitle}</div>
      ) : null}
      <TemplateMasonryGrid
        gridRef={gridRef}
        pending={!disableInnerPagination && isRevealingBatch}
        cellRise={!riseHold || riseAfterLoader}
        className={`mte-tpl-shop-grid mte-tpl-shop-grid--masonry mte-tpl-animate-tiles${playEnterAnim ? " mte-tpl-grid-enter" : ""}`}
        items={masonryItems}
        maxCols={Math.max(6, cols || 0)}
        device={T.device}
        renderItem={(p, index) => (
          <ShopProductCatalogCard
            product={p}
            index={index}
            isMobile={isMobile}
            showButton={showButton}
            showCategory={showCategory}
            showPrice={showPrice}
            buttonLabel={buttonLabel}
            brandCounts={brandCounts}
            onShopClick={() => {
              if (userId?.trim() && p.postId) {
                templateAnalytics?.trackShopClick({
                  postId: p.postId,
                  name: p.name,
                  sectionId: "productGrid",
                  shopView,
                  contentCategory: p.contentCategory,
                  categoryPrimary: browseLabels?.primary || p.breadcrumbs?.primary,
                  categorySecondary:
                    browseLabels?.secondary || p.breadcrumbs?.secondary,
                  categoryBrand: browseLabels?.brand || p.breadcrumbs?.brand,
                });
                void updateRefClick(p.postId, userId);
              }
            }}
          />
        )}
      />
      {((shownCount < products.length) ||
        (networkPaging && hasMore && shownCount >= products.length)) &&
        !disableInnerPagination && (
        <div ref={sentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
      )}
      </>
  );

  return (
    <div
      data-website-surface="shop-results"
      className={`mte-tpl-shop-catalog-stage${arriveThisPaint ? " is-arriving" : ""}`}
      aria-busy={refreshing || loading || undefined}
    >
      <div className="mte-tpl-shop-catalog-stage-grid">
        {disableInnerPagination ? (
          grid
        ) : (
          <TemplateFeedMediaRevealRoot plainMedia>
            {grid}
          </TemplateFeedMediaRevealRoot>
        )}
      </div>
    </div>
  );
}
